// DEV-794 / DEV-807 — school Google sign-in. Pure helpers, kept out of the
// pages so they can be unit-tested without loading auth0-js (same split as
// utils/skolon.ts for /login-skolon).

import type { LoginDoor, SchoolMatch } from '../apis/IdentifyApi';
import {
  GDPR_CONSENT_GIVEN,
  GDPR_FUNCTIONAL_ENABLED,
  GDPR_STATISTICS_ENABLED,
} from './constants';

export const SCHOOL_SSO_LOGIN_PATH = '/login-school';
// Where getRedirect sends a school-SSO pupil instead of /voucher. `from=app`
// tells /login-school to activate with the token it already has; every
// result other than an active subscription is a terminal screen.
export const SCHOOL_SSO_APP_RETURN_PATH = `${SCHOOL_SSO_LOGIN_PATH}?from=app`;

// Pupils at these schools are UK primary pupils. Used for the pre-set locale
// cookie when the deep link names none.
export const SCHOOL_SSO_FALLBACK_LOCALE = 'en-GB';

// The scope a pupil's authorize call asks for. Same choice as /login-pupil
// (DEV-736): keeps the child's name and email out of the id_token peikko
// receives. What Auth0 STORES is governed by the connection's own
// fetchUserProfile, not by this. activation needs only the `sub`.
export const SCHOOL_SSO_PUPIL_SCOPE = 'openid';

// The single pupil door that lets /school skip every screen and start SSO at
// once on a deep link (a pick in the typeahead shows the pupil-door screen
// instead, DEV-854): exactly ONE pupil door, its method is `google`, and it names its
// own connection. Anything else (several doors, another method, a google
// door with no connection) returns null and the normal flow runs.
//
// Exactly one, not "has a google door": with a choice of doors, picking one
// on the pupil's behalf would be guessing.
export const getAutoStartPupilDoor = (
  school?: Pick<SchoolMatch, 'loginDoors'> | null,
): (LoginDoor & { connection: string }) | null => {
  const doors = school?.loginDoors?.pupil;
  if (!Array.isArray(doors) || doors.length !== 1) return null;
  const [door] = doors;
  if (door?.method !== 'google') return null;
  const connection =
    typeof door.connection === 'string' ? door.connection.trim() : '';
  if (!connection) return null;
  return { method: 'google', connection };
};

export type PresetCookie = {
  name: string;
  value: string;
  days: number;
};

// The language cookie /school writes for a single-Google-door school, so a
// pupil on the auto-start path never sees /country. One year, like the
// school cookies written beside it: a 28-day locale would expire in the
// middle of term and drop a five-year-old on the country picker.
//
// This is ALL /school writes on top of the school cookies. Anyone can open
// the deep link — a parent, a teacher, someone forwarded the URL — so it
// must not stand in for anything a person agrees to.
export const getSchoolSsoLocaleCookie = (locale: string): PresetCookie => ({
  name: 'locale',
  value: locale,
  days: 365,
});

// ⚠️ NEEDS LEGAL/DPA SIGN-OFF BEFORE PROD. ⚠️
//
// The interstitial cookies /login-school writes on a pupil's behalf so a
// five-year-old on a managed Chromebook sees no onboarding, terms or
// cookie-consent screen: onboarding, terms (gdprVersion) and cookie consent
// (essential only: functional and statistics OFF). Today a person accepts
// the terms and the consent banner themselves; here the school's agreement
// with us stands in for both. That is a legal position, not an engineering
// one, and it has not been signed off yet — see the PR description. Do not
// ship to prod until it is.
//
// Written ONLY once activation has succeeded — the browser then holds a
// verified sub from the school's own pupil connection with a STUDENT seat
// on the school's voucher — and never by /school, which runs for whoever
// opens the link before any identity check (Fable review 2026-10-21).
//
// Values mirror what the interactive pages write: onboarding 'v1'
// (onboarding/2), gdprVersion = GDPR_VERSION (/terms), and the
// "essential only" button on /cookies/consent (consent given + functional
// false + statistics false). One year, like utils/cookie's own consent
// writers.
export const getSchoolSsoConsentCookies = (
  gdprVersion: string,
): PresetCookie[] => [
  { name: 'onboarding', value: 'v1', days: 365 },
  { name: 'gdprVersion', value: gdprVersion, days: 365 },
  { name: GDPR_CONSENT_GIVEN, value: gdprVersion, days: 365 },
  { name: GDPR_FUNCTIONAL_ENABLED, value: 'false', days: 365 },
  { name: GDPR_STATISTICS_ENABLED, value: 'false', days: 365 },
];

// Where a logged-out school-SSO pupil lands: /login-school showing one
// picture button and WAITING, like /login-skolon?from=logout. Auto-starting
// instead (what `/` does for a google-door device) signs the same pupil
// straight back in off their still-live Google session, so logout looks
// broken — and three logouts inside the start budget's window would end on
// the error screen.
//
// Same limit as Skolon's, stated precisely: this stops a SILENT re-login,
// not re-login. Logout is not federated, so the Google session stays live
// and one tap returns the previous pupil. On a shared device the next pupil
// must sign out of Google itself.
export const SCHOOL_SSO_LOGOUT_RETURN_PATH = `${SCHOOL_SSO_LOGIN_PATH}?from=logout`;

// How getRedirect knows `/` follows a logout rather than an expired
// session. /logout writes it for a school-SSO account (or a google-door
// device); the next successful activation clears it. A marker cookie
// rather than Auth0's `returnTo`: returnTo stays `/`, which is already an
// Allowed Logout URL on every tenant, and the routing decision stays in
// getRedirect with every other door. Skolon gets the same effect from its
// login-variant cookie, which a school-SSO pupil can't reuse: `/` must
// still auto-start for them after a token expiry, just not after a logout.
export const SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME = 'school_sso_logged_out';

// ---------------------------------------------------------------------------
// /login-school

// Every screen a pupil can end on. They all LOOK the same — a picture and
// "Ask your teacher" — because the reader is five; the distinction is for
// logs, analytics and the teacher/support who looks at `data-screen`.
export type SchoolSsoScreen =
  | 'notConfigured' // no connection resolved for this school
  | 'notReady' // 404 sso_not_configured: no voucher bound yet
  | 'noSeats' // 409 sso_seats_exhausted
  | 'ended' // 410 sso_access_ended
  | 'notAllowed' // 422 sso_product_not_allowed
  | 'error'; // anything else (403, 5xx, callback error, loop guard)

// Screens with a way forward: a picture-only "try again" button. The others
// are something only an adult can fix.
export const RETRYABLE_SCHOOL_SSO_SCREENS: SchoolSsoScreen[] = [
  'error',
  'notConfigured',
];

// jakari POST /user/sso/activate failure → screen. jakari answers
// `{ error, code, timestamp }`, passing identify's status through.
export const getSchoolSsoFailureScreen = (status?: number): SchoolSsoScreen => {
  if (status === 404) return 'notReady';
  if (status === 409) return 'noSeats';
  if (status === 410) return 'ended';
  if (status === 422) return 'notAllowed';
  return 'error';
};

export type SchoolSsoActivationOutcome =
  | { kind: 'handoff'; user: User; token: string }
  | {
      kind: 'screen';
      screen: SchoolSsoScreen;
      status?: number;
      code?: string;
      error?: unknown;
    };

// Only an ACTIVE subscription hands off. A 200 that is not active is the
// same terminal screen as a 410: handing it off would make getRedirect send
// the pupil straight back here.
export const resolveSchoolSsoActivation = async (
  activate: () => Promise<{ user: User; token: string }>,
): Promise<SchoolSsoActivationOutcome> => {
  let user: User;
  let token: string;
  try {
    ({ user, token } = await activate());
  } catch (err) {
    return {
      kind: 'screen',
      screen: getSchoolSsoFailureScreen(err?.status),
      status: err?.status,
      code: err?.code,
      error: err,
    };
  }
  if (user?.status !== 'active') {
    return { kind: 'screen', screen: 'ended', status: 200 };
  }
  return { kind: 'handoff', user, token };
};

// The Auth0 callback: a token or an error in the fragment.
export const isAuth0CallbackHash = (hash: string): boolean =>
  /(^|[#&])(id_token|access_token|error)=/.test(hash || '');

export type SchoolSsoEntryAction =
  | 'parseCallback'
  | 'showLoggedOut'
  | 'activateFromApp'
  | 'startSignIn';

// What /login-school does on arrival:
//   - Auth0 callback in the hash → parse it, then activate
//   - `from=logout` → the tap-to-sign-in screen; never start SSO unasked
//   - `from=app` with a token → activate straight away, no SSO
//   - otherwise → start SSO (bounded by the page's start budget), WITH OR
//     WITHOUT a token in the cookie.
//
// There is no "already signed in, hand off to `/`" shortcut (review
// 2026-10-26, round 4 B1). A token in the cookie says who signed in on this
// browser LAST, not who is at it now: when a pupil's server-side store and
// the clear after it both fail, the cookie still holds the previous pupil's
// HttpOnly token, and a reload of /login-school (or /school's lookup-error
// fallback, which lands here bare) would open that other child's session.
// A fresh SSO is what establishes the person — silent on a managed
// Chromebook — and its token goes through activation and the confirmed
// store like any first visit. Re-storing the leftover token would not help:
// it is the wrong child's.
export const getSchoolSsoEntryAction = ({
  hash,
  search,
  token,
}: {
  hash: string;
  search: string;
  token?: string | null;
}): SchoolSsoEntryAction => {
  if (isAuth0CallbackHash(hash)) return 'parseCallback';
  const from = new URLSearchParams(search || '').get('from');
  if (from === 'logout') return 'showLoggedOut';
  if (from === 'app' && token) return 'activateFromApp';
  return 'startSignIn';
};

// ---------------------------------------------------------------------------
// /login-staff (DEV-807)

export type StaffLoginMethod = 'microsoft' | 'google';

export type StaffLoginOption = {
  method: StaffLoginMethod;
  connection: string;
};

const RENDERABLE_STAFF_METHODS: StaffLoginMethod[] = ['microsoft', 'google'];

const sameConnection = (a?: string | null, b?: string | null): boolean =>
  Boolean(a) && Boolean(b) && a.toLowerCase() === b.toLowerCase();

// Which staff buttons /login-staff renders.
//
// A school's staff doors come from identify
// (`school_login_method.auth_connection`, e.g. `google-oauth2` for
// Crosthwaite). Each provider also has a deployment default:
// NEXT_PUBLIC_MICROSOFT_CONNECTION_NAME (one tenant-wide waad connection
// serves every Microsoft school) and NEXT_PUBLIC_GOOGLE_STAFF_CONNECTION_NAME
// (the generic `google-oauth2` social connection). Until 2026-10-30 Google
// was per school only; DEV-855 (Kaisa/Sergei) adds the default so a teacher
// who opens this page with no school chosen can still use Google.
//
//   - No school known (no cookie, no ?school=, or the lookup failed):
//     both defaults, Microsoft first, each only if its env var is set.
//   - School with staff doors: those doors, limited to what this page can
//     render (microsoft, google). A microsoft door uses its own connection,
//     else the Microsoft default. A google door needs its own connection;
//     without one it is dropped.
//   - School with no staff door we can render: the defaults, as above
//     (decided — a teacher must never face an empty page when there is a
//     door we can offer).
//
// ⚠️ Staff must NEVER go through a pupil connection (e.g.
// `crosthwaite-google`): a teacher's sub would then read as a pupil's and be
// activated on the pupil voucher. A staff door that names one of the
// school's pupil connections, or one on the deployment's school-SSO
// allow-list (`pupilConnections`, NEXT_PUBLIC_SCHOOL_SSO_CONNECTIONS), is
// ignored: a google door falls away, a microsoft door uses the default.
// The Google default gets the same check: if it ever names a pupil
// connection, it is dropped.
//
// Microsoft's default is exempt from that check. Microsoft staff and
// Microsoft pupils share one Auth0 connection by design (DEV-736: the
// voucher, not the connection, decides the role), so a "collision" there is
// expected and must not hide the staff Microsoft button.
export const getStaffLoginOptions = ({
  school,
  microsoftConnection,
  googleConnection,
  pupilConnections: knownPupilConnections = [],
}: {
  school?: Pick<SchoolMatch, 'loginDoors'> | null;
  microsoftConnection?: string;
  googleConnection?: string;
  pupilConnections?: string[];
}): StaffLoginOption[] => {
  const pupilConnections = [
    ...knownPupilConnections,
    ...(school?.loginDoors?.pupil ?? []).map((door) => door?.connection),
  ].filter(Boolean);
  const isPupilConnection = (connection?: string | null): boolean =>
    pupilConnections.some((pupil) => sameConnection(pupil, connection));

  // Per-method fallback for a door with no usable connection of its own.
  // Google's door keeps needing its own connection (see above); its default
  // only fills the no-door case below.
  const defaults: Record<StaffLoginMethod, string> = {
    microsoft: (microsoftConnection || '').trim(),
    google: '',
  };
  const googleDefault = (googleConnection || '').trim();

  const deploymentDefaults = (): StaffLoginOption[] => {
    const options: StaffLoginOption[] = [];
    if (defaults.microsoft) {
      options.push({ method: 'microsoft', connection: defaults.microsoft });
    }
    if (googleDefault && !isPupilConnection(googleDefault)) {
      options.push({ method: 'google', connection: googleDefault });
    }
    return options;
  };

  const staffDoors = school?.loginDoors?.staff;
  if (!school || !Array.isArray(staffDoors)) return deploymentDefaults();

  const seen = new Set<StaffLoginMethod>();
  const options: StaffLoginOption[] = [];
  staffDoors.forEach((door) => {
    const method = door?.method as StaffLoginMethod;
    if (!RENDERABLE_STAFF_METHODS.includes(method) || seen.has(method)) return;
    const ownConnection =
      typeof door.connection === 'string' ? door.connection.trim() : '';
    const own =
      ownConnection && !isPupilConnection(ownConnection) ? ownConnection : null;
    const connection = own ?? defaults[method];
    if (!connection) return;
    seen.add(method);
    options.push({ method, connection });
  });

  return options.length > 0 ? options : deploymentDefaults();
};
